/**
 * Inbox Component
 * Dropdown showing notifications (friend requests, shared playlists, feature updates)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  acceptFriendRequest,
  deleteNotification,
  dismissFeatureUpdate,
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
  rejectFriendRequest,
  type FeatureUpdate,
  type Notification,
} from '../api';

interface InboxProps {
  fullPage?: boolean;
}

export default function Inbox({ fullPage = false }: InboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [featureUpdates, setFeatureUpdates] = useState<FeatureUpdate[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await getNotifications();
      setNotifications(data.notifications);
      setFeatureUpdates(data.feature_updates);
    } catch {
      // Ignore errors
    }
  }, []);

  // Load unread count on mount and periodically
  useEffect(() => {
    let cancelled = false;

    const fetchUnreadCount = async () => {
      try {
        const count = await getUnreadNotificationCount();
        if (!cancelled) setUnreadCount(count);
      } catch {
        // Ignore errors
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Check every 30s

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Load full notifications when dropdown opens or in full page mode
  useEffect(() => {
    if (!isOpen && !fullPage) return;

    let cancelled = false;

    const fetchNotifications = async () => {
      setLoading(true);
      try {
        await loadNotifications();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchNotifications();

    return () => {
      cancelled = true;
    };
  }, [isOpen, fullPage, loadNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMarkRead = async (notificationId: number) => {
    try {
      await markNotificationRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Ignore errors
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // Ignore errors
    }
  };

  const handleDismiss = async (notificationId: number) => {
    try {
      await deleteNotification(notificationId);
      const notification = notifications.find((n) => n.id === notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      if (notification && !notification.read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch {
      // Ignore errors
    }
  };

  const handleAcceptFriend = async (notification: Notification) => {
    if (!notification.reference_id) return;

    // Optimistically remove from UI immediately
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    if (!notification.read) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }

    try {
      await acceptFriendRequest(notification.reference_id);
    } catch {
      // Revert on error
      setNotifications((prev) => [...prev, notification]);
      if (!notification.read) {
        setUnreadCount((prev) => prev + 1);
      }
    }
  };

  const handleRejectFriend = async (notification: Notification) => {
    if (!notification.reference_id) return;

    // Optimistically remove from UI immediately
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    if (!notification.read) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }

    try {
      await rejectFriendRequest(notification.reference_id);
    } catch {
      // Revert on error
      setNotifications((prev) => [...prev, notification]);
      if (!notification.read) {
        setUnreadCount((prev) => prev + 1);
      }
    }
  };

  const handleDismissFeatureUpdate = async (update: FeatureUpdate) => {
    // Optimistically remove from UI immediately
    setFeatureUpdates((prev) => prev.filter((u) => u.id !== update.id));
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      await dismissFeatureUpdate(update.id);
    } catch {
      // Revert on error
      setFeatureUpdates((prev) => [...prev, update]);
      setUnreadCount((prev) => prev + 1);
    }
  };

  const renderFeatureUpdate = (update: FeatureUpdate) => {
    return (
      <div key={update.id} className="inbox-item unread feature-update">
        <div className="inbox-item-content">
          <div className="feature-update-icon">
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
            </svg>
          </div>
          <div className="inbox-item-details">
            <p>
              <strong>{update.title}</strong>
            </p>
            <p className="feature-update-description">{update.description}</p>
            <span className="inbox-item-time">
              {new Date(update.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>
        <div className="inbox-item-actions">
          <button className="small secondary" onClick={() => handleDismissFeatureUpdate(update)}>
            Dismiss
          </button>
        </div>
      </div>
    );
  };

  const renderNotification = (notification: Notification) => {
    const fromUser = notification.from_user;
    const displayName = fromUser.username ? `@${fromUser.username}` : fromUser.name || 'Someone';

    if (notification.type === 'friend_request') {
      return (
        <div
          key={notification.id}
          className={`inbox-item ${notification.read ? 'read' : 'unread'}`}
        >
          <div className="inbox-item-content">
            {fromUser.picture ? (
              <img src={fromUser.picture} alt="" className="user-avatar small" />
            ) : (
              <div className="user-avatar small placeholder">
                {fromUser.username?.[0]?.toUpperCase() || '?'}
              </div>
            )}
            <div className="inbox-item-details">
              <p>
                <strong>{displayName}</strong> sent you a friend request
              </p>
              <span className="inbox-item-time">
                {new Date(notification.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className="inbox-item-actions">
            <button className="small" onClick={() => handleAcceptFriend(notification)}>
              Accept
            </button>
            <button className="small secondary" onClick={() => handleRejectFriend(notification)}>
              Decline
            </button>
          </div>
        </div>
      );
    }

    if (notification.type === 'playlist_shared') {
      return (
        <div
          key={notification.id}
          className={`inbox-item ${notification.read ? 'read' : 'unread'}`}
          onClick={() => !notification.read && handleMarkRead(notification.id)}
        >
          <div className="inbox-item-content">
            {fromUser.picture ? (
              <img src={fromUser.picture} alt="" className="user-avatar small" />
            ) : (
              <div className="user-avatar small placeholder">
                {fromUser.username?.[0]?.toUpperCase() || '?'}
              </div>
            )}
            <div className="inbox-item-details">
              <p>
                <strong>{displayName}</strong> shared a playlist with you
              </p>
              <span className="inbox-item-time">
                {new Date(notification.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className="inbox-item-actions">
            <Link to="/" className="small" onClick={() => setIsOpen(false)}>
              View
            </Link>
            <button
              className="small secondary"
              onClick={(e) => {
                e.stopPropagation();
                handleDismiss(notification.id);
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      );
    }

    if (notification.type === 'playlist_movie_added') {
      const movieTitle = (notification.metadata?.movie_title as string) || 'a movie';
      const playlistName = (notification.metadata?.playlist_name as string) || 'a playlist';
      const playlistId = notification.reference_id;

      return (
        <div
          key={notification.id}
          className={`inbox-item ${notification.read ? 'read' : 'unread'}`}
          onClick={() => !notification.read && handleMarkRead(notification.id)}
        >
          <div className="inbox-item-content">
            {fromUser.picture ? (
              <img src={fromUser.picture} alt="" className="user-avatar small" />
            ) : (
              <div className="user-avatar small placeholder">
                {fromUser.username?.[0]?.toUpperCase() || '?'}
              </div>
            )}
            <div className="inbox-item-details">
              <p>
                <strong>{displayName}</strong> added &lsquo;{movieTitle}&rsquo; to{' '}
                <strong>{playlistName}</strong>
              </p>
              <span className="inbox-item-time">
                {new Date(notification.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className="inbox-item-actions">
            {playlistId && (
              <Link
                to={`/playlist/${playlistId}`}
                className="small"
                onClick={() => setIsOpen(false)}
              >
                View
              </Link>
            )}
            <button
              className="small secondary"
              onClick={(e) => {
                e.stopPropagation();
                handleDismiss(notification.id);
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  // Full page mode - render as a standalone page
  if (fullPage) {
    return (
      <div className="inbox-page">
        <div className="inbox-page-header">
          <h2>Notifications</h2>
          {notifications.some((n) => !n.read) && (
            <button className="small secondary" onClick={handleMarkAllRead}>
              Mark all read
            </button>
          )}
        </div>

        <div className="inbox-page-list">
          {loading ? (
            <div className="inbox-empty">Loading...</div>
          ) : notifications.length === 0 && featureUpdates.length === 0 ? (
            <div className="inbox-empty">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                width="48"
                height="48"
              >
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
              </svg>
              <p>No notifications</p>
            </div>
          ) : (
            <>
              {featureUpdates.map(renderFeatureUpdate)}
              {notifications.map(renderNotification)}
            </>
          )}
        </div>
      </div>
    );
  }

  // Dropdown mode - render as a header dropdown
  return (
    <div className="inbox-container" ref={dropdownRef}>
      <button
        className="inbox-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          width="20"
          height="20"
        >
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="inbox-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="inbox-dropdown">
          <div className="inbox-header">
            <h3>Notifications</h3>
            {notifications.some((n) => !n.read) && (
              <button className="small secondary" onClick={handleMarkAllRead}>
                Mark all read
              </button>
            )}
          </div>

          <div className="inbox-list">
            {loading ? (
              <div className="inbox-empty">Loading...</div>
            ) : notifications.length === 0 && featureUpdates.length === 0 ? (
              <div className="inbox-empty">No notifications</div>
            ) : (
              <>
                {featureUpdates.map(renderFeatureUpdate)}
                {notifications.map(renderNotification)}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
